import React, { useMemo } from 'react';
import Person from '@mui/icons-material/Person';
import ArrowBack from '@mui/icons-material/ArrowBack';
import Home from '@mui/icons-material/Home';
import { Box, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import UniversalSidebar, { SidebarSection, SidebarItem } from './UniversalSidebar';
import ServerSelection from '../ServerSelection';
import { useAuth } from '../../contexts/AuthContext';
import { canAccessQaPage } from '../../config/qaPermissions';
import { RoleChip } from '../ui';

interface QaSidebarProps {
  mobileOpen: boolean;
  onDrawerToggle: () => void;
  /** «Вернуться в кабинет» не влезла в шапку — показать её первым пунктом меню */
  showBackToDashboard?: boolean;
  /** Сайдбар свёрнут на десктопе, чтобы контент занимал всю ширину. */
  collapsed?: boolean;
}


// 1:1 with AdminSidebar: тот же бейдж роли, та же подстановка «Вернуться в кабинет» первым пунктом,
// отличается только меню (единственный пункт).
const QaSidebar: React.FC<QaSidebarProps> = ({
  mobileOpen,
  onDrawerToggle,
  showBackToDashboard,
  collapsed = false,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  // Тот же порог, что у бургера: ниже 870px шапка сворачивается и «Главная» из неё пропадает
  const isMobile = useMediaQuery('(max-width: 869px)');

  // Бейдж роли — тот же чип, что в админке и в списке пользователей.
  // useMemo: иначе новый JSX на каждый рендер сбрасывает мемоизацию секций сайдбара.
  const QaInfo = useMemo(() => (
    <Box sx={{ textAlign: 'center', mb: 2 }}>
      {user?.role && (
        <RoleChip role={user.role} label={t(`admin:users.roles.${user.role.toLowerCase()}`)} />
      )}
    </Box>
  ), [t, user?.role]);

  const menuItems = useMemo((): SidebarItem[] => {
    const items: SidebarItem[] = [
      { id: 'accounts', text: t('qa:navigation.accounts'), icon: <Person />, path: '/qa/accounts' },
    ].filter(item => canAccessQaPage(user?.role, item.id));

    // Иначе из QA-зоны на узком экране не выйти: в меню ссылок наружу нет
    if (showBackToDashboard) {
      items.unshift({
        id: 'back-to-dashboard',
        text: t('admin:navigation.backToDashboard'),
        icon: <ArrowBack />,
        path: '/dashboard',
      });
    }

    // «Главная» — как в админке и кабинете: на узком экране уезжает в конец меню
    if (isMobile) {
      items.push({
        id: 'home',
        text: t('navigation:menu.home'),
        icon: <Home />,
        path: '/',
      });
    }
    return items;
  }, [t, user?.role, showBackToDashboard, isMobile]);

  const sections = useMemo((): SidebarSection[] => [
    {
      id: 'qa-info-section',
      customContent: QaInfo,
      fixed: true,
    },
    {
      id: 'server-section',
      customContent: <ServerSelection />,
      fixed: true,
    },
    {
      id: 'menu-section',
      withMenuSection: true,
      items: menuItems,
    },
  ], [menuItems, QaInfo]);

  return (
    <UniversalSidebar
      sections={sections}
      mobileOpen={mobileOpen}
      onDrawerToggle={onDrawerToggle}
      storageKey="qaSidebarExpanded"
      collapsed={collapsed}
    />
  );
};

export default QaSidebar;
